import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useSite } from "../context/SiteContext";
import PageHero from "../components/PageHero";
import AccommodationCard from "../components/AccommodationCard";
import BookingDialog from "../components/BookingDialog";
import MultiRoomBookingDialog from "../components/MultiRoomBookingDialog";
import { BocauxMenu } from "../components/BocauxMenu";
import Reveal from "../components/Reveal";
import { Coffee, UtensilsCrossed, Backpack, MapPin } from "lucide-react";
import { BikeRental } from "../components/BikeRental";

const NEARBY = [
  { name: "Dol-de-Bretagne", time: "5 min" },
  { name: "Mont-Saint-Michel", time: "30 min" },
  { name: "Saint-Malo", time: "25 min" },
  { name: "Cancale", time: "25 min" },
  { name: "Dinan", time: "30 min" },
];

export default function ChambresPage() {
  const { t } = useI18n();
  const { settings } = useSite();
  const [items, setItems] = useState([]);
  const [booking, setBooking] = useState(null);
  const [multiOpen, setMultiOpen] = useState(false);

  useEffect(() => {
    api.get("/accommodations", { params: { type: "chambre" } }).then((r) => setItems(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <PageHero title={t("chambresPage.title")} subtitle={t("hero.subtitle")} image={settings.chambres_hero || "/api/files/begaudiere/uploads/8ae94926-002b-462d-b38f-64159689b16f.webp"} crumb={t("nav.chambres")} />
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center mb-8">
          <Reveal><p className="text-ink-600 font-body text-lg leading-relaxed">{t("chambresPage.intro")}</p></Reveal>
        </div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mb-10 flex justify-center">
          <button onClick={() => setMultiOpen(true)} data-testid="open-multi-booking"
            className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-6 py-3 text-sm font-semibold hover:bg-brand-600 transition-colors shadow-sm">
            Réserver plusieurs chambres en un seul paiement
          </button>
        </div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((a, i) => (
            <Reveal key={a.id} delay={(i % 3) * 100} className="h-full"><AccommodationCard item={a} onBook={setBooking} /></Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-beige/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <img src="/petit-dejeuner.webp" alt="Petit déjeuner à la Bégaudière" loading="lazy" decoding="async" className="rounded-2xl w-full aspect-[4/3] object-cover" />
          </Reveal>
          <Reveal delay={120}>
            <div className="w-14 h-14 rounded-full bg-brand-50 flex items-center justify-center mb-4"><Coffee size={24} strokeWidth={1.5} className="text-brand" /></div>
            <h2 className="font-heading text-3xl sm:text-4xl text-ink-900 mb-4">{t("chambresPage.breakfast")}</h2>
            <p className="text-ink-600 font-body leading-relaxed">{t("chambresPage.breakfastText")}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <Reveal className="lg:order-2">
              <img src="/api/files/begaudiere/uploads/08b036a5-2b72-4d6c-8231-1405803ec50f.webp" alt="Les bocaux, La Cuisine de Mon Père" loading="lazy" decoding="async" className="rounded-2xl w-full aspect-[4/3] object-cover" />
            </Reveal>
            <Reveal delay={120} className="lg:order-1">
              <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-4 py-1.5 text-xs font-medium tracking-wide text-brand mb-4">
                <UtensilsCrossed size={15} strokeWidth={1.75} /> {t("chambresPage.mealsTag")}
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl text-ink-900 mb-4">{t("chambresPage.mealsTitle")}</h2>
              <p className="text-ink-600 font-body leading-relaxed mb-6">{t("chambresPage.mealsText")}</p>
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="rounded-2xl bg-white border border-black/5 p-5">
                  <p className="font-body text-sm text-ink-600 mb-1">{t("chambresPage.mealEntree")}</p>
                  <p className="font-heading text-2xl sm:text-3xl text-brand">{t("chambresPage.mealEntreePrice")}</p>
                </div>
                <div className="rounded-2xl bg-white border border-black/5 p-5">
                  <p className="font-body text-sm text-ink-600 mb-1">{t("chambresPage.mealPlat")}</p>
                  <p className="font-heading text-2xl sm:text-3xl text-brand">{t("chambresPage.mealPlatPrice")}</p>
                </div>
                <div className="rounded-2xl bg-white border border-black/5 p-5">
                  <p className="font-body text-sm text-ink-600 mb-1">{t("chambresPage.mealDessert")}</p>
                  <p className="font-heading text-2xl sm:text-3xl text-brand">{t("chambresPage.mealDessertPrice")}</p>
                </div>
              </div>
              <div className="mb-6">
                <BocauxMenu />
              </div>
              <div className="flex items-start gap-3 rounded-2xl bg-brand-50 p-5">
                <Backpack size={22} strokeWidth={1.5} className="text-brand shrink-0 mt-0.5" />
                <div>
                  <p className="font-heading text-lg text-ink-900">{t("chambresPage.picnicTitle")}</p>
                  <p className="text-ink-600 font-body text-sm leading-relaxed">{t("chambresPage.picnicText")}</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white" data-testid="chambres-seo-section">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl text-ink-900 mb-6">{t("chambresPage.seoTitle")}</h2>
            <div className="space-y-4 text-ink-600 font-body leading-relaxed">
              <p>{t("chambresPage.seoP1")}</p>
              <p>{t("chambresPage.seoP2")}</p>
              <p>{t("chambresPage.seoP3")}</p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h3 className="font-heading text-xl text-ink-900 mt-10 mb-4">{t("chambresPage.nearbyTitle")}</h3>
            <div className="flex flex-wrap gap-2.5">
              {NEARBY.map((n) => (
                <span key={n.name} className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 text-brand px-4 py-2 text-sm font-medium">
                  <MapPin size={14} strokeWidth={1.75} /> {n.name} · {n.time}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <BikeRental dark />
      <BookingDialog item={booking} open={!!booking} onOpenChange={(v) => !v && setBooking(null)} />
      <MultiRoomBookingDialog rooms={items} open={multiOpen} onOpenChange={setMultiOpen} />
    </div>
  );
}
